import { Spacing, Text } from '@vkontakte/vkui';
import imageB from 'img/crossPromoVkMusicFakedoor/1TbAndMusic.png';
import imageBDark from 'img/crossPromoVkMusicFakedoor/1TbAndMusicDark.png';
import imageA from 'img/crossPromoVkMusicFakedoor/1TbSale.png';
import imageADark from 'img/crossPromoVkMusicFakedoor/1TbSaleDark.png';
import imageCD from 'img/crossPromoVkMusicFakedoor/vkMusic.png';
import imageCDDark from 'img/crossPromoVkMusicFakedoor/vkMusicDark.png';
import React, { memo, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import {
    crossPromoVkMusicFakedoor,
    crossPromoVkMusicFakedoorTariff,
} from 'reactApp/appHelpers/featuresHelpers/features/crossPromoVkMusicFakedoor';
import { WhatsNewDialog } from 'reactApp/components/WhatsNewDialog/WhatsNewDialog';
import { getPaySource } from 'reactApp/modules/payment/payment.analytics';
import { openTariffBuy } from 'reactApp/modules/payment/payment.module';
import { productsController } from 'reactApp/modules/products/products.controller';
import { getProductById } from 'reactApp/modules/products/products.selectors';
import { sendVkMusicPromocodeRequest } from 'reactApp/modules/vkMusic/vkMusic.saga';
import type { RootState } from 'reactApp/store';
import type { Product } from 'reactApp/types/Billing';
import { renderCrossPromoVkMusicFakedoorModalThanks } from 'reactApp/ui/CrossPromoVkMusicFakedoorModal/CrossPromoVkMusicFakedoorModalThanks/CrossPromoVkMusicFakedoorModalThanks.helpers';
import { renderCrossPromoVkMusicSeveralSubscriptions } from 'reactApp/ui/CrossPromoVkMusicFakedoorModal/CrossPromoVkMusicSeveralSubscriptions/CrossPromoVkMusicSeveralSubscriptions.helpers';
import { isDarkThemeModeEnabled } from 'reactApp/utils/theme';

import styles from './CrossPromoVkMusicFakedoorModal.css';
import type { Props } from './CrossPromoVkMusicFakedoorModal.types';

const imageMap = {
    a: imageA,
    b: imageB,
    c: imageCD,
    d: imageCD,
};

const darkImageMap = {
    a: imageADark,
    b: imageBDark,
    c: imageCDDark,
    d: imageCDDark,
};

const primaryButtonTextMap = {
    a: 'Увеличить со скидкой',
    b: 'Увеличить со скидкой',
    c: 'Добавить со скидкой',
    d: 'Добавить со скидкой',
};

const titleMap = {
    a: 'Увеличьте свой тариф\u00A0со скидкой 10%',
    b: 'Увеличьте свой тариф со\u00A0скидкой\u00A010% и\u00A0получите\u00A0подписку на VK\u00A0Музыку',
    c: 'Добавьте к своей подписке VK\u00A0Музыку со\u00A0скидкой\u00A050%',
    d: 'Добавьте к своей подписке VK\u00A0Музыку со\u00A0скидкой\u00A070%',
};

const getText = (variant, priceToUpgrade) => {
    const textMap = {
        a: `Доплатите ${priceToUpgrade >= 0 ? priceToUpgrade : 0} ₽ в месяц и получите 1\u00A0ТБ в\u00A0Облаке`,
        b: `Доплатите ${
            priceToUpgrade >= 0 ? priceToUpgrade : 0
        } ₽ в месяц и получите 1\u00A0ТБ в\u00A0Облаке + слушайте VK Музыку без\u00A0ограничений, пока у\u00A0вас есть Mail\u00A0Space`,
        c: 'Доплатите 99 ₽ вместо 199 ₽ и получите сразу две подписки. Специальная\u00A0цена\u00A0действует,\u00A0пока\u00A0у\u00A0вас есть Mail\u00A0Space',
        d: 'Доплатите 59 ₽ вместо 199 ₽ и получите сразу две подписки. Cпециальная\u00A0цена\u00A0действует,\u00A0пока\u00A0у\u00A0вас есть Mail\u00A0Space',
    };

    return textMap[variant];
};

export const CrossPromoVkMusicFakedoorModal = memo<Props>(({ onClick, onShow, onClose, onSecondary, onCrossClick, price }) => {
    const dispatch = useDispatch();
    const product: Product | undefined = useSelector((state: RootState) => getProductById(state, crossPromoVkMusicFakedoorTariff));

    useEffect(() => {
        onShow();
        emitAnalyticEvent(AnalyticEventNames.CROSS_PROMO_VK_MUSIC_FAKEDOOR_SHOW);
    }, []);

    useEffect(() => {
        if (crossPromoVkMusicFakedoor === 'a' || crossPromoVkMusicFakedoor === 'b') {
            productsController.loadProducts();
        }
    }, []);

    const handleClick = useCallback(() => {
        onClick();
        onClose();

        emitAnalyticEvent(AnalyticEventNames.CROSS_PROMO_VK_MUSIC_FAKEDOOR_CLICK);

        if (crossPromoVkMusicFakedoor === 'a') {
            dispatch(
                openTariffBuy({
                    productId: crossPromoVkMusicFakedoorTariff,
                    checkAnonymous: true,
                    paySource: getPaySource('upsale_control'),
                    hideOtherTariffs: true,
                    onSuccess: () => {
                        renderCrossPromoVkMusicSeveralSubscriptions();
                    },
                })
            );
        } else {
            dispatch(sendVkMusicPromocodeRequest());
            renderCrossPromoVkMusicFakedoorModalThanks();
        }
    }, [dispatch, onClick, onClose]);

    const handleSecondary = useCallback(() => {
        onSecondary();
        onClose();
    }, [onClose, onSecondary]);
    const handleClose = useCallback(() => {
        onCrossClick?.();
        onClose();
    }, [onClose, onCrossClick]);

    if (!crossPromoVkMusicFakedoor) {
        return null;
    }

    const containsDarkTheme = isDarkThemeModeEnabled();

    const content = (
        <>
            <Spacing size={16} />
            <Text className={styles.text} weight="3">
                {getText(crossPromoVkMusicFakedoor, product?.price ? product.price - price : 0)}
            </Text>
            <Spacing size={32} />
        </>
    );

    let closeIconClassName: string | undefined;
    if (crossPromoVkMusicFakedoor === 'a' && !containsDarkTheme) {
        closeIconClassName = styles.closeIconLight;
    } else if (crossPromoVkMusicFakedoor !== 'a' && containsDarkTheme) {
        closeIconClassName = styles.closeIconDark;
    }

    return (
        <WhatsNewDialog
            primaryButtonText={primaryButtonTextMap[crossPromoVkMusicFakedoor]}
            secondaryButtonText="Не сейчас"
            title={titleMap[crossPromoVkMusicFakedoor]}
            imageUrl={containsDarkTheme ? darkImageMap[crossPromoVkMusicFakedoor] : imageMap[crossPromoVkMusicFakedoor]}
            content={content}
            dialogSize="large"
            buttonTheme="vk"
            onClick={handleClick}
            onClose={handleClose}
            onSecondary={handleSecondary}
            closeIconClassName={closeIconClassName}
        />
    );
});

CrossPromoVkMusicFakedoorModal.displayName = 'CrossPromoVkMusicFakedoorModal';
